import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createUser, getCurrentUser, updateUser } from '../api/user';

export const createNewUser = createAsyncThunk(
  'user/create',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await createUser(userData);
      if (!response) {
        throw new Error('No data received from createUser');
      }
      return response;
    } catch (error) {
      console.error('Create user error:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  'user/fetchCurrent',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCurrentUser();
      // if (!response.data) {
      //   throw new Error('No data received from getCurrentUser');
      // }
      return response; // Return the nested user object
    } catch (error) {
      console.error('Fetch current user error:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateCurrentUser = createAsyncThunk(
  'user/update',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await updateUser(userData);
      if (!response) {
        throw new Error('No data received from updateUser');
      }
      return response;
    } catch (error) {
      console.error('Update user error:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Initial state: Must align with incoming user fields
const initialState = {
  id: null,
  email: null,
  portfolio_id: null,
  subscription_status: null,
  stripe_customer_id: null,
  nextBillingDate: null,
  is_active: null,
  status: 'idle',  // idle | loading | succeeded | failed
  error: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearUser: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Create User
      .addCase(createNewUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createNewUser.fulfilled, (state, action) => {
        console.log('Create user fulfilled payload:', action.payload);
        if (!action.payload) {
          state.status = 'failed';
          state.error = 'No user data received';
          return;
        }
        state.status = 'succeeded';
        state.id = action.payload.id;
        state.email = action.payload.email;
        state.portfolio_id = action.payload.portfolio_id;
        state.subscription_status = action.payload.subscription_status;
        state.stripe_customer_id = action.payload.stripe_customer_id;
        state.nextBillingDate = action.payload.nextBillingDate;
        state.is_active = action.payload.is_active;
      })
      .addCase(createNewUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to create user';
      })
      
      // Fetch Current User
      .addCase(fetchCurrentUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        // If no user data is returned for any reason, mark error
        if (!action.payload) {
          state.status = 'failed';
          state.error = 'No user data received';
          return;
        }

        // Otherwise, update the slice state with the incoming user data
        state.status = 'succeeded';
        state.id = action.payload.id || null;
        state.email = action.payload.email || null;
        state.portfolio_id = action.payload.portfolio_id || null;
        state.subscription_status = action.payload.subscription_status || null;
        state.stripe_customer_id = action.payload.stripe_customer_id || null;
        state.nextBillingDate = action.payload.nextBillingDate || null;
        state.is_active = action.payload.is_active || null;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch user';
      })
      
      // Update Current User
      .addCase(updateCurrentUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateCurrentUser.fulfilled, (state, action) => {
        console.log('Update user fulfilled payload:', action.payload);
        if (!action.payload) {
          state.status = 'failed';
          state.error = 'No user data received';
          return;
        }
        state.status = 'succeeded';
        state.id = action.payload.id;
        state.email = action.payload.email;
        state.portfolio_id = action.payload.portfolio_id;
        state.subscription_status = action.payload.subscription_status;
        state.stripe_customer_id = action.payload.stripe_customer_id;
        state.nextBillingDate = action.payload.nextBillingDate;
        state.is_active = action.payload.is_active;
      })
      .addCase(updateCurrentUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to update user';
      });
  },
});

export const { clearUser } = userSlice.actions;
export default userSlice.reducer;